import React, { useCallback, useMemo } from "react";
import { Box, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import type { LineCustomSvgLayer, LineSeries } from "@nivo/line";
import dayjs from "dayjs";
import GenericFinancialLineChart from "./shared/GenericFinancialLineChart";
import ChartTooltip from "./shared/ChartTooltip";
import {
  ActualResult,
  EstimateLevel,
  EstimateRange,
  getEstimateSeries,
} from "./analystEstimatesData";

export const ESTIMATE_LEVELS: { key: EstimateLevel; label: string }[] = [
  { key: "low", label: "Low" },
  { key: "average", label: "Average" },
  { key: "high", label: "High" },
];

interface AnalystEstimatesChartProps {
  actuals: ActualResult[];
  ranges: EstimateRange[];
  metricLabel: string;
  colors: Record<EstimateLevel | "actual", string>;
  formatValue: (value: number | null) => string;
}

export const AnalystEstimatesChart: React.FC<AnalystEstimatesChartProps> = ({
  actuals,
  ranges,
  metricLabel,
  colors,
  formatValue,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isNarrow = useMediaQuery(theme.breakpoints.down(360));
  const chartData = useMemo(
    () => getEstimateSeries(actuals, ranges),
    [actuals, ranges],
  );
  const periods = useMemo(() => [...actuals, ...ranges], [actuals, ranges]);
  const maxTicks = isNarrow ? 3 : isMobile ? 5 : 10;
  const tickStep = Math.max(1, Math.ceil(periods.length / maxTicks));
  const ticks = periods
    .filter((_, index) => index % tickStep === 0)
    .map((result) => result.period);
  const finalPeriod = periods[periods.length - 1]?.period;
  if (finalPeriod && ticks[ticks.length - 1] !== finalPeriod) {
    ticks[ticks.length - 1] = finalPeriod;
  }
  const anchor = actuals[actuals.length - 1];

  // nivo mounts custom layers as components, so their identity must only
  // change with the data they draw or every render remounts the SVG subtree.
  const ForecastLines = useCallback<LineCustomSvgLayer<LineSeries>>(
    ({ series, lineGenerator, xScale, innerHeight }) => {
      const anchorX = anchor ? xScale(anchor.period) : 0;
      return (
        <g>
          {anchor && (
            <line
              x1={anchorX}
              x2={anchorX}
              y1={0}
              y2={innerHeight}
              stroke={theme.palette.divider}
              strokeDasharray="3 5"
            />
          )}
          {series.map((serie) => {
            const isActual = serie.id === "actual";
            const dash = isActual ? undefined : "2 6";
            return (
              <path
                key={serie.id}
                d={
                  lineGenerator(serie.data.map((point) => point.position)) ??
                  undefined
                }
                fill="none"
                stroke={serie.color}
                strokeWidth={isActual ? 2.75 : 2.25}
                strokeDasharray={dash}
                strokeLinecap="round"
                strokeLinejoin="round"
                pointerEvents="none"
              />
            );
          })}
        </g>
      );
    },
    [anchor, theme],
  );

  const PeriodDetails = useCallback<LineCustomSvgLayer<LineSeries>>(
    ({ xScale, yScale, innerWidth, innerHeight }) => (
      <g>
        {periods.map((result, index) => {
          const x = xScale(result.period);
          const previous = periods[index - 1];
          const next = periods[index + 1];
          const left = previous ? (xScale(previous.period) + x) / 2 : 0;
          const right = next ? (xScale(next.period) + x) / 2 : innerWidth;
          const isActual = "value" in result;
          const rows = isActual
            ? [
                {
                  label: `Actual ${metricLabel}`,
                  value: formatValue(result.value),
                  color: colors.actual,
                },
              ]
            : ESTIMATE_LEVELS.map(({ key, label }) => ({
                label,
                value: formatValue(result[key]),
                color: colors[key],
              }));
          const values = isActual
            ? [{ key: "actual" as const, value: result.value }]
            : ESTIMATE_LEVELS.map(({ key }) => ({ key, value: result[key] }));
          const label = `${result.period} ${metricLabel}: ${rows.map((row) => `${row.label.toLowerCase()} ${row.value}`).join(", ")}`;
          if (!isActual && result.analysts !== null)
            rows.push({
              label: "Analysts",
              value: String(result.analysts),
              color: "",
            });
          return (
            <Tooltip
              key={result.date}
              describeChild
              disableInteractive
              placement="top"
              enterTouchDelay={0}
              leaveTouchDelay={4000}
              title={
                <ChartTooltip
                  header={`${result.period} · ${dayjs(result.date).format("MMM D, YYYY")}`}
                  rows={rows}
                />
              }
              slotProps={{
                tooltip: {
                  sx: { bgcolor: "transparent", p: 0, maxWidth: "none" },
                },
              }}
            >
              <g
                role="button"
                tabIndex={0}
                aria-label={label}
                style={{ cursor: "help", outline: "none" }}
              >
                <rect
                  className="estimate-focus"
                  x={left}
                  y={0}
                  width={Math.max(1, right - left)}
                  height={innerHeight}
                  fill="transparent"
                  rx={3}
                />
                {values
                  .filter((point) => point.value !== null)
                  .map((point) => (
                    <circle
                      key={point.key}
                      cx={x}
                      cy={yScale(point.value)}
                      r={result.date === anchor?.date ? 4.5 : 3}
                      fill={theme.palette.background.paper}
                      stroke={colors[point.key]}
                      strokeWidth={2}
                      pointerEvents="none"
                    />
                  ))}
              </g>
            </Tooltip>
          );
        })}
      </g>
    ),
    [periods, anchor, metricLabel, formatValue, colors, theme],
  );

  return (
    <Box
      role="group"
      aria-label={`${metricLabel} actual results and low, average and high analyst forecasts`}
      sx={{
        "& g[role=button]:focus-visible .estimate-focus": {
          stroke: theme.palette.primary.main,
          strokeWidth: 1,
        },
      }}
    >
      <GenericFinancialLineChart
        title={`${metricLabel} forecast`}
        renderCard={false}
        noHover
        chartData={chartData}
        isLoading={false}
        error={null}
        isEmpty={false}
        height={isMobile ? 320 : 380}
        colors={(serie) => colors[serie.id as keyof typeof colors]}
        curve="linear"
        pointSize={0}
        lineLayer={ForecastLines}
        topLayers={[PeriodDetails]}
        yAxisFormat={formatValue}
        xAxisTickRotation={0}
        axisBottomTickValues={ticks}
        leftMargin={isMobile ? 66 : 80}
        rightMargin={32}
        bottomMargin={36}
      />
    </Box>
  );
};
